import { Dispatch } from 'react';

// biome-ignore lint/suspicious/noExplicitAny: reason
export type GenericFormData = { [key: string]: any };

export type TouchedFields<FormData extends GenericFormData> = {
  [Field in keyof FormData]?: boolean | TouchedFields<FormData[Field][number]>[];
};

export type FormState<FormData extends GenericFormData> = {
  formData: FormData;
  touchedFields: TouchedFields<FormData>;
};

type SimpleFieldUpdate<
  FormData extends GenericFormData,
  Field extends keyof FormData = keyof FormData,
> = {
  field: Field;
  value: FormData[Field];
};

export type SimpleFieldUpdateAction<FormData extends GenericFormData> = {
  type: 'simpleUpdate';
  payload: SimpleFieldUpdate<FormData>;
};

export type AddRepeatedFieldAction<
  FormData extends GenericFormData,
  Field extends keyof FormData = keyof FormData,
> = {
  type: 'addRepeated';
  payload: {
    field: Field;
    value: FormData[Field][number];
  };
};

export type UpdateRepeatedFieldAction<
  FormData extends GenericFormData,
  Field extends keyof FormData = keyof FormData,
> = {
  type: 'updateRepeated';
  payload: {
    field: Field;
    index: number;
    update: SimpleFieldUpdate<FormData[Field][number]>;
  };
};

export type RemoveRepeatedFieldAction<
  FormData extends GenericFormData,
  Field extends keyof FormData = keyof FormData,
> = {
  type: 'removeRepeated';
  payload: {
    field: Field;
    index: number;
  };
};

export type SetRepeatedFieldAction<
  FormData extends GenericFormData,
  Field extends keyof FormData = keyof FormData,
> = {
  type: 'setRepeated';
  payload: {
    field: Field;
    index: number;
    value: FormData[Field][number];
  };
};

export type ResetFormAction<FormData extends GenericFormData> = {
  type: 'reset';
  payload: FormData;
};

export type Action<FormData extends GenericFormData> =
  | SimpleFieldUpdateAction<FormData>
  | AddRepeatedFieldAction<FormData>
  | RemoveRepeatedFieldAction<FormData>
  | SetRepeatedFieldAction<FormData>
  | UpdateRepeatedFieldAction<FormData>
  | ResetFormAction<FormData>;

export type DispatchFieldChange<FormData extends GenericFormData> = Dispatch<Action<FormData>>;

export type FieldValidator<FormData extends GenericFormData> = (
  data: FormData,
) => undefined | string | string[] | true;

export type FieldCondition<FormData extends GenericFormData> = (data: FormData) => boolean;

interface BaseValidationFieldConfig<FormData extends GenericFormData> {
  required: boolean;
  condition?: FieldCondition<FormData>;
}
export interface SimpleValidationFieldConfig<FormData extends GenericFormData>
  extends BaseValidationFieldConfig<FormData> {
  type: 'simple';
  requiredErrorMessage?: string;
  fieldIsPresent?: (data: FormData) => boolean;
  validate?: FieldValidator<FormData>;
}

export interface RepeatedValidationFieldConfig<
  FormData extends GenericFormData,
  Field extends keyof FormData = keyof FormData,
> extends BaseValidationFieldConfig<FormData> {
  type: 'repeated';
  schema?: ValidationConfig<FormData[Field][number]>;
}

export type ValidationFieldConfig<
  FormData extends GenericFormData,
  Field extends keyof FormData = keyof FormData,
> = SimpleValidationFieldConfig<FormData> | RepeatedValidationFieldConfig<FormData, Field>;

export type ValidationConfig<FormData extends GenericFormData> = {
  [Field in keyof FormData]?: ValidationFieldConfig<FormData, Field>;
};

export type ValidationError<
  FormData extends GenericFormData,
  Field extends keyof FormData = keyof FormData,
> = boolean | string | string[] | ValidationErrors<FormData[Field][number]>[];
export type ValidationErrors<FormData extends GenericFormData> = {
  [Field in keyof FormData]?: ValidationError<FormData, Field>;
};

export type GenerateErrorsResponse<FormData extends GenericFormData> = {
  errors: ValidationErrors<FormData>;
  counters?: Counters;
};

export type Counters = {
  missingRequiredFields: number;
  requiredFields: number;
};
export interface UseFormResponse<FormData extends GenericFormData> {
  formData: FormData;
  isValid: boolean;
  isPristine: boolean;
  dispatch: DispatchFieldChange<FormData>;
  errors: ValidationErrors<FormData>;
  trySubmit: (doSubmit: () => void) => void;
  resetForm: () => void;
  touchedFields: TouchedFields<FormData>;
  counters: Counters;
}

export enum ValidationMode {
  OnSubmit, // delays all field validation until submit
  RequiredOnSubmit, // delays required field validation until submit

  OnEdit,
}
